import {
  Credentials,
  Permission,
  Permissions,
  Service,
  Terms,
  Account as UserAccount,
} from "@theprelude/sdk";
import {
  AuthSession,
  AuthUser,
  fetchAuthSession,
  getCurrentUser,
  signIn,
  signOut,
} from "aws-amplify/auth";
import { createJSONStorage, persist } from "zustand/middleware";
import { createWithEqualityFn as create } from "zustand/traditional";

export const getServiceURL = () =>
  localStorage.getItem("serviceURL") ??
  import.meta.env.VITE_PRELUDE_SERVICE_URL;

const requestInterceptor = (request: Request) => {
  request.headers.set("_product", "js-sdk");
  return request;
};

export interface AuthConfig {
  host: string;
  credentials?: Credentials;
  status?: "authenticated" | "unauthenticated";
  handle?: string;
}

export class NewPasswordRequiredError extends Error {
  constructor() {
    super("New password required");
    this.name = "NewPasswordRequiredError";
  }
}

interface AuthStore {
  user?: AuthUser;
  session?: AuthSession;

  currentAccount?: string;
  changeAccount: (accountId?: string) => void;
  getAuthConfig: () => Promise<AuthConfig>;
  signIn: ({
    email,
    password,
  }: {
    email: string;
    password: string;
  }) => Promise<void>;
  signOut: (params?: { clearAccount?: boolean }) => Promise<void>;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      user: undefined,
      session: undefined,
      currentAccount: undefined,
      changeAccount: (accountId?: string) => {
        set({ currentAccount: accountId });
      },
      getAuthConfig: async () => {
        const state = get();

        try {
          const session = await fetchAuthSession();
          const user = await getCurrentUser();
          const token = session.tokens?.accessToken?.toString();

          if (!token) {
            throw new Error("No token found");
          }

          set({ session, user });

          return {
            host: getServiceURL(),
            status: "authenticated",
            handle: session.tokens?.idToken?.payload.email as string,
            credentials: {
              token,
              account: state.currentAccount,
            },
          };
        } catch {
          set({
            session: undefined,
            user: undefined,
          });
          return {
            host: getServiceURL(),
            status: "unauthenticated",
          };
        }
      },

      signIn: async ({
        email,
        password,
      }: {
        email: string;
        password: string;
      }) => {
        const { getAuthConfig } = get();

        const { nextStep } = await signIn({ username: email, password });

        if (
          nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
        ) {
          throw new NewPasswordRequiredError();
        }

        const auth = await getAuthConfig();

        if (auth.status === "unauthenticated") {
          throw new Error("Sign in failed");
        }
      },
      signOut: async (params?: { clearAccount?: boolean }) => {
        await signOut({
          global: false,
          oauth: {},
        });

        set({
          session: undefined,
          currentAccount: params?.clearAccount
            ? undefined
            : get().currentAccount,
          user: undefined,
        });
      },
    }),
    {
      name: `prelude:auth`,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ currentAccount: state.currentAccount }),
    },
  ),
);

export const authState = () => useAuthStore.getState();

export const service = (authConfig: AuthConfig) =>
  new Service({
    host: authConfig.host,
    credentials: authConfig.credentials,
    requestInterceptor,
  });

export const useAuthConfig = (): AuthConfig => {
  const token = useAuthStore((state) =>
    state.session?.tokens?.accessToken?.toString(),
  );
  const handle = useAuthStore(
    (state) => state.session?.tokens?.idToken?.payload.email as string,
  );
  const account = useAuthStore((state) => state.currentAccount);

  if (!token) {
    return {
      host: getServiceURL(),
      status: "unauthenticated",
    };
  }

  return {
    host: getServiceURL(),
    status: "authenticated",
    handle,
    credentials: {
      token,
      account,
    },
  };
};

export const isCurrentUserPerm = (perm: Permission, account?: UserAccount) => {
  return (
    account?.users.some(
      (user) => isUserMatch(account.whoami, user) && user.permission === perm,
    ) ?? false
  );
};

export const isAdminUser = (account?: UserAccount) => {
  return isCurrentUserPerm(Permissions.ADMIN, account);
};

export function isUserMatch(
  left: { handle: string; oidc: string },
  right: { handle: string; oidc: string },
): boolean {
  return left.handle === right.handle && left.oidc === right.oidc;
}

export function authConfig() {
  return {
    host: getServiceURL(),
    credentials: {
      token: authState().session?.tokens?.idToken,
      account: authState().currentAccount,
    },
  };
}

export const checkUserTerms = ({
  terms,
  version,
  account,
}: {
  terms: keyof Terms;
  version: string;
  account?: UserAccount;
}) => {
  return (
    account?.users.some(
      (user) =>
        isUserMatch(account.whoami, user) &&
        Object.hasOwn(user.terms?.[terms] ?? {}, version),
    ) ?? false
  );
};
